import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import { Modal, ModalBody, ModalFooter } from 'reactstrap';
import { selectNeedsTimezone, setSelectedAgencyTimezone } from 'slices/auth/reducer';
import { updateAgencyTimezone } from 'services/api/auth';
import { AppDispatch, RootState } from 'store';
import { Timezone } from 'types';
import { getErrorMessage } from 'utils/helpers';
import './style.scss';

const getDeviceTimezone = () => {
    try {
        return Intl.DateTimeFormat().resolvedOptions().timeZone;
    } catch {
        return '';
    }
};

const pickDefaultTimezoneId = (timezoneList: Timezone[]) => {
    if (timezoneList.length === 0) return null;

    const deviceTimezone = getDeviceTimezone();
    const match = timezoneList.find(
        (timezone) => timezone.iana_code.toLowerCase() === deviceTimezone.toLowerCase()
    );

    return match?.id ?? timezoneList[0].id;
};

const SelectTimezoneModal: React.FC = () => {
    const dispatch = useDispatch<AppDispatch>();
    const needsTimezone = useSelector(selectNeedsTimezone);
    const timezoneList = useSelector((state: RootState) => state.auth.master?.timezone_list ?? []);
    const selectedAgencyId = useSelector((state: RootState) => state.auth.selectedAgencyId);
    const [selectedId, setSelectedId] = useState<number | null>(() => pickDefaultTimezoneId(timezoneList));
    const [submitting, setSubmitting] = useState(false);

    useEffect(() => {
        setSelectedId((current) => {
            if (current && timezoneList.some((timezone) => timezone.id === current)) {
                return current;
            }
            return pickDefaultTimezoneId(timezoneList);
        });
    }, [timezoneList]);

    const selectedTimezone = useMemo(
        () => timezoneList.find((timezone) => timezone.id === selectedId) ?? null,
        [selectedId, timezoneList]
    );

    const handleSubmit = async () => {
        if (!selectedTimezone || submitting || !selectedAgencyId) return;

        try {
            setSubmitting(true);
            const response = await updateAgencyTimezone(selectedAgencyId, selectedTimezone.id);
            const timezoneFromResponse = response?.timezone as Partial<Timezone> | undefined;
            dispatch(setSelectedAgencyTimezone(
                timezoneFromResponse?.id
                    ? {
                        id: timezoneFromResponse.id,
                        iana_code: timezoneFromResponse.iana_code ?? selectedTimezone.iana_code,
                        name: timezoneFromResponse.name ?? selectedTimezone.name,
                        utc_offset: timezoneFromResponse.utc_offset ?? selectedTimezone.utc_offset,
                    }
                    : selectedTimezone
            ));
        } catch (error) {
            toast.error(getErrorMessage(error, 'Failed to save timezone. Please try again.'));
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <Modal
            centered
            isOpen={needsTimezone}
            backdrop="static"
            keyboard={false}
        >
            <ModalBody>
                <div className="select-timezone-modal">
                    <div className="select-timezone-modal__content">
                        <div className="select-timezone-modal__heading">
                            <div className="select-timezone-modal__icon" aria-hidden="true">
                                <i className="ri-global-line"></i>
                            </div>
                            Set your timezone
                        </div>

                        <div className="select-timezone-modal__list" role="listbox" aria-label="Timezone">
                            {timezoneList.map((timezone) => {
                                const isSelected = timezone.id === selectedId;

                                return (
                                    <button
                                        key={timezone.id}
                                        type="button"
                                        role="option"
                                        aria-selected={isSelected}
                                        className={`select-timezone-modal__option ${isSelected ? 'is-selected' : ''}`}
                                        onClick={() => setSelectedId(timezone.id)}
                                    >
                                        <span className="select-timezone-modal__option-copy">
                                            <span className="select-timezone-modal__option-name">{timezone.name}</span>
                                            <span className="select-timezone-modal__option-offset">{timezone.iana_code} {timezone.utc_offset}</span>
                                        </span>
                                        <span className={`select-timezone-modal__radio ${isSelected ? 'is-selected' : ''}`} />
                                    </button>
                                );
                            })}
                        </div>
                    </div>
                </div>
            </ModalBody>
            <ModalFooter className="border-top py-3">
                <button
                    type="button"
                    className="theme-btn btn-primary w-100"
                    disabled={!selectedTimezone || submitting}
                    onClick={handleSubmit}
                >
                    {submitting ? 'Saving...' : 'Continue'}
                </button>
            </ModalFooter>
        </Modal>
    );
};

export default SelectTimezoneModal;
